/**
 * <collection-mega-menu>
 *
 * Switches the variant panel shown on the right when a product on the left is hovered or focused.
 * Standalone on purpose: it does not depend on any theme class, so it cannot break the stock Impact menus.
 */
(function () {
  if (window.customElements.get('collection-mega-menu')) return;

  const HOVER_DELAY = 60;

  class CollectionMegaMenu extends HTMLElement {
    connectedCallback() {
      this._items = Array.from(this.querySelectorAll('[data-cmm-product]'));
      this._panels = Array.from(this.querySelectorAll('[data-cmm-panel]'));

      if (this._items.length === 0 || this._panels.length === 0) return;

      this._activeIndex = 0;
      this._hoverTimer = null;
      this._onPointerEnter = this._onPointerEnter.bind(this);
      this._onFocus = this._onFocus.bind(this);
      this._onPointerLeave = this._onPointerLeave.bind(this);

      this._items.forEach((item) => {
        item.addEventListener('pointerenter', this._onPointerEnter);
        item.addEventListener('focus', this._onFocus);
      });

      this.addEventListener('pointerleave', this._onPointerLeave);

      // Reset to the first product whenever the parent menu closes, so it always reopens in a known state.
      this._details = this.closest('details');

      if (this._details) {
        this._observer = new MutationObserver(() => {
          if (!this._details.hasAttribute('open')) this.activate(0, true);
        });

        this._observer.observe(this._details, { attributes: true, attributeFilter: ['open'] });
      }

      this.activate(0, true);
    }

    disconnectedCallback() {
      clearTimeout(this._hoverTimer);

      if (this._observer) this._observer.disconnect();

      if (this._items) {
        this._items.forEach((item) => {
          item.removeEventListener('pointerenter', this._onPointerEnter);
          item.removeEventListener('focus', this._onFocus);
        });
      }

      this.removeEventListener('pointerleave', this._onPointerLeave);
    }

    _indexOf(element) {
      const index = parseInt(element.getAttribute('data-cmm-product'), 10);
      return Number.isNaN(index) ? null : index;
    }

    _onPointerEnter(event) {
      const index = this._indexOf(event.currentTarget);
      if (index === null) return;

      clearTimeout(this._hoverTimer);
      this._hoverTimer = setTimeout(() => this.activate(index), HOVER_DELAY);
    }

    _onFocus(event) {
      const index = this._indexOf(event.currentTarget);
      if (index === null) return;

      clearTimeout(this._hoverTimer);
      this.activate(index);
    }

    _onPointerLeave() {
      clearTimeout(this._hoverTimer);
    }

    activate(index, force) {
      if (index === this._activeIndex && force !== true) return;
      if (!this._panels[index] || !this._items[index]) return;

      this._items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });

      this._panels.forEach((panel, panelIndex) => {
        if (panelIndex === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      // Images are lazy by default; promote the ones now on screen.
      this._panels[index]
        .querySelectorAll('img[loading="lazy"]')
        .forEach((image) => image.setAttribute('loading', 'eager'));

      this._activeIndex = index;
    }
  }

  window.customElements.define('collection-mega-menu', CollectionMegaMenu);

  /* -------------------------------------------------------------------------------------------------------------------
     MOBILE INLINE DROPDOWN
     ---------------------------------------------------------------------------------------------------------------------

     A <details> hides its non-summary content entirely while closed, so the content has no rendered "0fr" frame to
     transition away from. Setting `open` and the open state in the same frame therefore makes the browser snap partway
     before the transition picks up — that is the hitch at the start of the expand.

     The sequence below fixes it:

       open:  set `open`  ->  wait two frames (content is now rendered and laid out at 0fr)  ->  add `.is-open`
       close: remove `.is-open`  ->  wait for the transition to actually end  ->  remove `open`

     The open state lives ONLY on `.is-open` in the CSS — never on `[open]` — which is what makes step one possible.

     Everything is event-delegated from the document, so dropdowns rendered later (Section Rendering API, design mode)
     work with no init pass and no document-wide MutationObserver re-querying on every DOM change.
  ------------------------------------------------------------------------------------------------------------------- */

  const DROPDOWN_FALLBACK = 600;
  const dropdownState = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getState(details) {
    let state = dropdownState.get(details);

    if (!state) {
      state = { timer: null, onEnd: null, frame: null };
      dropdownState.set(details, state);
    }

    return state;
  }

  function clearPending(details) {
    const state = getState(details);
    const content = details.querySelector('.cmm-mobile-dropdown__content');

    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }

    if (state.frame) {
      cancelAnimationFrame(state.frame);
      state.frame = null;
    }

    if (state.onEnd && content) {
      content.removeEventListener('transitionend', state.onEnd);
      state.onEnd = null;
    }
  }

  function openDropdown(details) {
    const content = details.querySelector('.cmm-mobile-dropdown__content');
    if (!content) return;

    clearPending(details);
    details.setAttribute('open', '');

    // Give the images their final size before the height animates, so the target height never moves mid-transition.
    content.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute('loading', 'eager'));

    if (prefersReducedMotion()) {
      details.classList.add('is-open');
      return;
    }

    // Two frames: the first renders the content at 0fr, the second starts the transition from that measured state.
    const state = getState(details);

    state.frame = requestAnimationFrame(() => {
      state.frame = requestAnimationFrame(() => {
        state.frame = null;
        details.classList.add('is-open');
      });
    });
  }

  function closeDropdown(details) {
    const content = details.querySelector('.cmm-mobile-dropdown__content');
    if (!content) return;

    clearPending(details);
    details.classList.remove('is-open');

    if (prefersReducedMotion()) {
      details.removeAttribute('open');
      return;
    }

    const state = getState(details);

    const finish = () => {
      clearPending(details);
      details.removeAttribute('open');
    };

    state.onEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'grid-template-rows') return;
      finish();
    };

    content.addEventListener('transitionend', state.onEnd);

    // Safety net: transitionend does not fire if the element is hidden mid-animation (drawer closed, panel switched).
    state.timer = setTimeout(finish, DROPDOWN_FALLBACK);
  }

  function toggleDropdown(details) {
    if (details.classList.contains('is-open')) {
      closeDropdown(details);
    } else {
      openDropdown(details);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest('.cmm-mobile-dropdown__summary');
      if (!summary) return;

      const details = summary.closest('.cmm-mobile-dropdown__details');
      if (!details) return;

      // Stop the native details toggle so the two-step sequence above stays in control.
      event.preventDefault();
      toggleDropdown(details);
    },
    true
  );

  // Collapse every dropdown when the drawer closes, so it reopens in a known state.
  document.addEventListener('dialog:after-hide', (event) => {
    if (!event.target.querySelectorAll) return;

    event.target.querySelectorAll('.cmm-mobile-dropdown__details').forEach((details) => {
      clearPending(details);
      details.classList.remove('is-open');
      details.removeAttribute('open');
    });
  });
})();
